import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Btn } from "./UI";

const GENDERS = ["", "Female", "Male", "Non-binary", "Prefer not to say", "Other"];

/**
 * Quick-create modal for new clients. Opens as a lightweight overlay so a front-desk
 * user never leaves the current page (e.g. mid-way through a note or scheduler flow).
 *
 * Required fields per product spec: first_name, last_name, dob, and (phone OR email).
 * On save, POSTs to /api/clients and fires onCreated(newClient).
 */
export function QuickClientModal({ open, onClose, onCreated, initialName = "" }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", gender: "",
    phone: "", email: "",
  });
  const [busy, setBusy] = useState(false);
  const [dupCandidates, setDupCandidates] = useState([]);

  useEffect(() => {
    if (open) {
      // Split initialName like "Jane Doe" into first/last so the typed prefix is not lost
      const parts = (initialName || "").trim().split(/\s+/).filter(Boolean);
      setForm({
        first_name: parts[0] || "",
        last_name: parts.slice(1).join(" ") || "",
        dob: "", gender: "", phone: "", email: "",
      });
      setDupCandidates([]);
    }
  }, [open, initialName]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e, forceCreate = false) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!form.first_name.trim()) return toast.error("First name is required");
    if (!form.last_name.trim()) return toast.error("Last name is required");
    if (!form.dob) return toast.error("Date of birth is required");
    if (!form.phone.trim() && !form.email.trim()) {
      return toast.error("A phone number or an email is required");
    }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        dob: form.dob,
        gender: form.gender || "",
        phone: form.phone.trim(),
        email: form.email.trim(),
        status: "active",
      };
      const url = forceCreate ? "/clients?force=true" : "/clients";
      const r = await api.post(url, payload);
      toast.success(`${r.data.first_name} ${r.data.last_name} added`);
      if (typeof onCreated === "function") onCreated(r.data);
      onClose();
    } catch (err) {
      // Backend returns 409 with candidate list when a duplicate is detected. Show the
      // matches inline so the front desk can pick an existing chart or force-create.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && detail.code === "duplicate_client") {
        setDupCandidates(detail.candidates || []);
        toast.warning("A client with the same name and date of birth already exists");
      } else {
        toast.error(apiError(err));
      }
    } finally { setBusy(false); }
  };

  const pickExisting = (candidate) => {
    if (typeof onCreated === "function") onCreated(candidate);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[85]" data-testid="quick-client-modal">
      <div className="absolute inset-0 bg-[#3E2723]/40 backdrop-blur-[2px]" onClick={busy ? undefined : onClose} />
      <div className="absolute left-1/2 top-1/2 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[#EFEBE0] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#F1EEE7] bg-[#FDFCF8] px-5 py-4 rounded-t-2xl">
          <div>
            <h3 className="font-heading text-base font-bold text-[#3E2723]">Quick Add Client</h3>
            <p className="mt-0.5 text-[11px] text-[#7B6157]">Create a chart in one keystroke. You can fill the rest later from Intake.</p>
          </div>
          <button type="button" data-testid="quick-client-close" aria-label="Close" onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#F5F2EB]">
            <X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">First name *</label>
              <input type="text" data-testid="quick-client-first-name" value={form.first_name}
                onChange={(e) => setF("first_name", e.target.value)} autoFocus
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">Last name *</label>
              <input type="text" data-testid="quick-client-last-name" value={form.last_name}
                onChange={(e) => setF("last_name", e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">Date of birth *</label>
              <input type="date" data-testid="quick-client-dob" value={form.dob}
                onChange={(e) => setF("dob", e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">Gender</label>
              <select data-testid="quick-client-gender" value={form.gender}
                onChange={(e) => setF("gender", e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]">
                {GENDERS.map((g) => <option key={g || "unspecified"} value={g}>{g || "Not specified"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">Phone</label>
              <input type="tel" data-testid="quick-client-phone" value={form.phone}
                onChange={(e) => setF("phone", e.target.value)} placeholder="e.g., 555-000-1212 (10+ digits)"
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-[#6D4C41] tracking-wide">Email</label>
              <input type="email" data-testid="quick-client-email" value={form.email}
                onChange={(e) => setF("email", e.target.value)} placeholder="name@example.com"
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
          </div>
          <div className="rounded-lg border border-[#EDE7DC] bg-[#FDFCF8] px-3 py-2 text-[11px] text-[#6D4C41]">
            One of phone or email is required. Everything else can be added later from the client&apos;s Intake or Personal tabs.
          </div>
          {dupCandidates.length > 0 && (
            <div className="rounded-lg border border-[#FFB300] bg-[#FFF8E1] px-3 py-3" data-testid="quick-client-dup-panel">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#8D5A00]">Possible duplicate detected</div>
              <div className="text-[11px] text-[#6D4C41] mb-2">A client with the same name and date of birth already exists. Reuse the existing chart, or create a second one only if you are sure they are different people.</div>
              <div className="space-y-1.5">
                {dupCandidates.map((c) => (
                  <button type="button" key={c.id} data-testid={`quick-client-dup-${c.id}`}
                    onClick={() => pickExisting(c)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#EDE7DC] bg-white px-3 py-2 text-left hover:bg-[#F5F2EB]">
                    <div>
                      <div className="text-sm font-semibold text-[#3E2723]">{c.first_name} {c.last_name}</div>
                      <div className="text-[10px] text-[#7B6157]">DOB {c.dob} · MRN {c.mrn} · {c.status || "active"}{c.phone_last4 ? " · phone ****" + c.phone_last4 : ""}</div>
                    </div>
                    <span className="text-[11px] font-bold text-[#7B6157]">Use this chart</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
            {dupCandidates.length > 0 ? (
              <Btn type="button" data-testid="quick-client-force-create"
                onClick={() => submit(null, true)} disabled={busy}
                className="border-[#C62828] bg-[#C62828] text-white hover:bg-[#B71C1C]">
                {busy ? "Creating…" : "Create anyway (new chart)"}
              </Btn>
            ) : (
              <Btn type="submit" data-testid="quick-client-save" disabled={busy}>
                {busy ? "Adding…" : "Add and Select"}
              </Btn>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
